/**
 * Per-category visual style preferences.
 *
 *   vector  — original 1979 line-art look: thin stroke, per-type glow, no fill.
 *   shaded  — modern lit look: gradient/photoreal fill, rim light, drop shadow.
 *   mesh    — full WebGL 3D meshes via the dynamic-imported overlay (three.js).
 *             Falls back to shaded at render time until the overlay loads so
 *             a stale localStorage value or slow CDN can't blank the game.
 *
 * Four independent categories — asteroid, ship, bullet, particle — so a
 * player can mix-and-match (e.g. mesh asteroids on a vector ship). The
 * settings panel offers a quick-preset shortcut (ALL CLASSIC / ALL SHADED /
 * ALL 3D) for the common case.
 *
 * Fresh installs use mesh with standard presentation. Quality is a separate
 * automatic budget for pixels, effects and a low-tier 2D fallback. Saved art
 * choices return when the frame budget recovers.
 */

import { QUALITY_BUDGETS, QualityGovernor, type QualityLevel, type QualityPreference } from './quality.js';
import type { Asteroid, PowerUp, Ship, Ufo } from './types.js';
import { ASCII_COLS, BIT_DEPTH, coerceThemeId, type ThemeId } from './postfx/index.js';

export type VisualTier = 'vector' | 'shaded' | 'mesh';
export type VisualCategory = 'asteroid' | 'ship' | 'bullet' | 'particle';

export const VISUAL_CATEGORIES: readonly VisualCategory[] = ['asteroid', 'ship', 'bullet', 'particle'];
export const VISUAL_TIERS: readonly VisualTier[] = ['vector', 'shaded', 'mesh'];

const KEY = 'pallasite:visualStyle';

interface State {
  asteroid: VisualTier;
  ship: VisualTier;
  bullet: VisualTier;
  particle: VisualTier;
  theme: ThemeId;
  asciiCols: number;
  bitDepth: number;
  bitColour: boolean;
  brightness: number;
}

export const BRIGHTNESS = { min: 0.7, max: 1.35, step: 0.05, default: 1.0 } as const;

function defaults(): State {
  // Fresh installs open in full 3D mesh with the standard presentation.
  // CRT remains selectable, but it is no longer the default: the extra
  // full-frame post-process cost is not worth paying before a player opts in.
  return {
    asteroid: 'mesh',
    ship: 'mesh',
    bullet: 'mesh',
    particle: 'mesh',
    theme: 'none',
    asciiCols: ASCII_COLS.default,
    bitDepth: BIT_DEPTH.default,
    bitColour: false,
    brightness: BRIGHTNESS.default,
  };
}

let cached: State | null = null;

let cachedSearch: string | null = null;
let cachedParams = new URLSearchParams();

function urlFlag(name: string): string | null {
  try {
    if (cachedSearch !== window.location.search) {
      cachedSearch = window.location.search;
      cachedParams = new URLSearchParams(cachedSearch);
    }
    return cachedParams.get(name);
  }
  catch { return null; }
}

export function mobileRuntimeActive(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  // Dev/test escape hatch: force every mobile code path (shaded render, DPR cap,
  // direct music output, .m4a, deferred boot loads) on a desktop browser, so
  // iPhone-only issues can be reproduced without a device. Joins the existing
  // override family (?fullfx, ?mobileLite, ?directMusic, ?webaudioMusic).
  if (urlFlag('forceMobile') === '1') return true;
  const ua = navigator.userAgent;
  const uaMobile = /iPhone|iPad|iPod|Android/i.test(ua);
  // Modern iPadOS Safari reports a desktop "Macintosh" UA with no iPad token,
  // and an iPad in landscape exceeds the small-viewport gate below — so without
  // an explicit check an iPad falls through to the full desktop path (mesh +
  // DPR 2 + post-FX) and cannot hold frame budget even on wave 1. This is also
  // why our recent mobile perf work appeared to do nothing on iPads: every
  // guard keys off this function, and it was answering "not mobile". A real Mac
  // reports maxTouchPoints 0 (a trackpad is not a touch digitiser); >1 touch
  // point under a Mac UA is the only reliable iPad-as-Mac tell.
  const iPadAsMac = /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  const coarse = (() => {
    try { return window.matchMedia?.('(pointer: coarse)').matches === true; }
    catch { return false; }
  })();
  const touch = (navigator.maxTouchPoints ?? 0) > 0;
  const smallViewport = Math.min(window.innerWidth || 9999, window.innerHeight || 9999) <= 640
    || Math.max(window.innerWidth || 0, window.innerHeight || 0) <= 960;
  return uaMobile || iPadAsMac || ((coarse || touch) && smallViewport);
}

/** URL escape hatches that force FULL fidelity regardless of device or
 *  measured performance — `?fullfx=1` / `?highfx=1` for capture/debug, and
 *  `?mobileLite=0` to disable the phone guard. Both must also veto the runtime
 *  governor below, or the override wouldn't actually hold under load. */
function fxOverrideForcesFull(): boolean {
  return urlFlag('fullfx') === '1' || urlFlag('highfx') === '1' || urlFlag('mobileLite') === '0';
}

/** `?mobile3d=1` opts a phone into the live 3D renderer (shadows off) so the
 *  frame cost can be measured on a real device before it becomes a default. */
export function mobile3dRequested(): boolean {
  return urlFlag('mobile3d') === '1';
}

/** `?mobileShadows=1` (with `?mobile3d=1`) also turns the shadow map on, so
 *  UFOs and the ship shade the rocks beneath them as they do on desktop. */
export function mobileShadowsRequested(): boolean {
  return urlFlag('mobileShadows') === '1';
}

/** Phone-safe presentation guard. iOS Safari cannot reliably hold frame
 *  budget with the WebGL mesh overlay plus the game canvas in the general
 *  campaign, so phones use the shaded canvas path by default and skip
 *  expensive presentation FX. Sanctum is the exception: its textured fillers
 *  and council medallions are cheaper and clearer on the overlay path. */
export function mobilePerformanceGuardActive(): boolean {
  return !fxOverrideForcesFull() && mobileRuntimeActive();
}

const QUALITY_KEY = 'pallasite:quality';
const governor = new QualityGovernor(mobileRuntimeActive() ? 'balanced' : 'high');
let qualityPreference: QualityPreference = (() => {
  try {
    const saved = localStorage.getItem(QUALITY_KEY);
    if (saved === 'low' || saved === 'balanced' || saved === 'high') return saved;
  } catch { /* storage is optional */ }
  return 'auto';
})();

export function getQualityPreference(): QualityPreference { return qualityPreference; }
export function getQualityLevel(): QualityLevel {
  if (fxOverrideForcesFull()) return 'high';
  return qualityPreference === 'auto' ? governor.level : qualityPreference;
}

function notifyQuality(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('pallasite:quality'));
  if (getQualityLevel() !== 'low') void warmWebGLIfPreviouslyEnabled();
}

export function setQualityPreference(value: QualityPreference): void {
  qualityPreference = value;
  governor.resetSamples();
  try { localStorage.setItem(QUALITY_KEY, value); } catch { /* storage is optional */ }
  notifyQuality();
}

export function getRenderDprCap(): number {
  const budget = QUALITY_BUDGETS[getQualityLevel()];
  // Bound total pixels as well as DPR: retina desktop and tablet canvases
  // otherwise multiply the cost of both the 2D and WebGL surfaces.
  const area = typeof window === 'undefined' ? 1280 * 720
    : Math.max(1280 * 720, window.innerWidth * window.innerHeight);
  return Math.min(budget.dpr, Math.sqrt(budget.pixels / area));
}

export function getParticleBudget(): number { return QUALITY_BUDGETS[getQualityLevel()].particles; }

export function recordFrameTime(ms: number): void {
  if (qualityPreference !== 'auto' || fxOverrideForcesFull()) return;
  if (governor.sample(ms)) notifyQuality();
}

export function resetFrameTimes(): void { governor.resetSamples(); }

/** Optional glow and full-frame filters yield before gameplay clarity. */
export function reducedFxActive(): boolean { return getQualityLevel() !== 'high'; }

function effectiveTier(_cat: VisualCategory, tier: VisualTier): VisualTier {
  // At the lowest budget, stop paying mesh draw-call/driver costs as well
  // as fill-rate. This rescues software WebGL and old GPUs; the stored mesh
  // choice returns automatically when the governor recovers a higher tier.
  return tier === 'mesh' && getQualityLevel() === 'low' ? 'shaded' : tier;
}

/** Coerce an unknown value into a known VisualTier; unknown → 'vector'. */
function coerceTier(v: unknown): VisualTier {
  if (v === 'vector' || v === 'shaded' || v === 'mesh') return v;
  return 'vector';
}

/** Coerce an unknown value into a valid ASCII column count; clamps to the
 *  supported range, unknown → the default. */
function coerceAsciiCols(v: unknown): number {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.max(ASCII_COLS.min, Math.min(ASCII_COLS.max, n)) : ASCII_COLS.default;
}

/** Coerce an unknown value into a valid bit-depth stop; unknown or
 *  off-grid → the default. */
function coerceBitDepth(v: unknown): number {
  const n = Math.round(Number(v));
  return (BIT_DEPTH.stops as readonly number[]).includes(n) ? n : BIT_DEPTH.default;
}

function coerceBrightness(v: unknown): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return BRIGHTNESS.default;
  const stepped = Math.round(n / BRIGHTNESS.step) * BRIGHTNESS.step;
  return Math.max(BRIGHTNESS.min, Math.min(BRIGHTNESS.max, stepped));
}

function load(): State {
  if (cached) return cached;
  const base = defaults();
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      cached = {
        asteroid: coerceTier(parsed.asteroid ?? base.asteroid),
        ship: coerceTier(parsed.ship ?? base.ship),
        bullet: coerceTier(parsed.bullet ?? base.bullet),
        particle: coerceTier(parsed.particle ?? base.particle),
        theme: coerceThemeId(parsed.theme ?? base.theme),
        asciiCols: coerceAsciiCols(parsed.asciiCols ?? base.asciiCols),
        bitDepth: coerceBitDepth(parsed.bitDepth ?? base.bitDepth),
        bitColour: typeof parsed.bitColour === 'boolean' ? parsed.bitColour : base.bitColour,
        brightness: coerceBrightness(parsed.brightness ?? base.brightness),
      };
      return cached;
    }
  } catch { /* localStorage might be blocked — fall through to defaults */ }
  cached = base;
  return cached;
}

function save(s: State): void {
  cached = s;
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore */ }
  // Broadcast so live render code can react without re-reading every frame.
  // The settings panel listens for this to repaint button selection.
  try { window.dispatchEvent(new CustomEvent('pallasite:visualStyle')); } catch { /* ignore */ }
}

let forcedTier: VisualTier | null = null;

/** Force a single tier for the duration of one synchronous render()
 *  call. The watch live tiles use this to render in vector regardless
 *  of the player's saved preference: set it, call render(), clear it.
 *  Synchronous use only, so it never races other render() callers. */
export function setForcedVisualTier(tier: VisualTier | null): void {
  forcedTier = tier;
}

/** Read the effective tier for a category. Honours an active forced
 *  tier override (see setForcedVisualTier). Render code falls back to
 *  'shaded' if the WebGL overlay hasn't loaded yet. */
export function getVisualStyle(cat: VisualCategory): VisualTier {
  if (forcedTier) return forcedTier;
  return effectiveTier(cat, load()[cat]);
}

/** The raw stored tier, ignoring any forced override. Used by the
 *  settings UI so it reflects the player's real saved choice. */
export function getVisualStyleRaw(cat: VisualCategory): VisualTier {
  return load()[cat];
}

/** Set a category's tier. If the new tier is 'mesh', kick off the
 *  WebGL overlay dynamic-load so it's ready by the next frame (or one
 *  shortly after). Fire-and-forget — render code falls back to shaded
 *  while the load resolves. */
export function setVisualStyle(cat: VisualCategory, tier: VisualTier): void {
  const next = { ...load(), [cat]: tier };
  save(next);
  if (effectiveTier(cat, tier) === 'mesh') void warmWebGL();
}

/** Set every category to the same tier. Used by the quick-pick row in the
 *  settings panel. */
export function setAllVisualStyles(tier: VisualTier): void {
  save({ ...load(), asteroid: tier, ship: tier, bullet: tier, particle: tier });
  if (VISUAL_CATEGORIES.some((cat) => effectiveTier(cat, tier) === 'mesh')) void warmWebGL();
}

/** The active presentation theme: the post-process look (CRT etc.). A
 *  separate axis from the per-category fidelity tiers. Suppressed whenever
 *  reduced-FX is active — the phone guard OR the runtime governor on an
 *  overloaded device — since full-frame post-FX is one of the costs we shed. */
export function getTheme(): ThemeId {
  if (reducedFxActive()) return 'none';
  return load().theme;
}

/** Set the presentation theme. Persists and broadcasts like the tiers. */
export function setTheme(theme: ThemeId): void {
  save({ ...load(), theme });
}

/** The ASCII theme's character-grid resolution (column count). */
export function getAsciiCols(): number {
  return load().asciiCols;
}

/** Set the ASCII resolution. Clamped to the supported range; persists and
 *  broadcasts like the other visual prefs. */
export function setAsciiCols(cols: number): void {
  save({ ...load(), asciiCols: coerceAsciiCols(cols) });
}

/** The bit-depth theme's colour depth (bits per pixel). */
export function getBitDepth(): number {
  return load().bitDepth;
}

/** Set the bit-depth theme's colour depth. Snapped to a valid stop;
 *  persists and broadcasts like the other visual prefs. */
export function setBitDepth(depth: number): void {
  save({ ...load(), bitDepth: coerceBitDepth(depth) });
}

/** The bit-depth theme's palette mode — true colour, false greyscale. */
export function getBitColour(): boolean {
  return load().bitColour;
}

/** Set the bit-depth theme's palette mode. Persists and broadcasts. */
export function setBitColour(on: boolean): void {
  save({ ...load(), bitColour: on });
}

/** Canvas brightness multiplier. Applied as a compositor filter so the
 *  standard presentation stays cheap; the default 1.0 is visually neutral. */
export function getBrightness(): number {
  return load().brightness;
}

export function setBrightness(value: number): void {
  save({ ...load(), brightness: coerceBrightness(value) });
}

/** Boot-time warm-up: if any category is already on 'mesh' from a
 *  previous session, start the WebGL load immediately. Called from
 *  main.ts; safe to call repeatedly (the loader is idempotent). */
export function warmWebGLIfPreviouslyEnabled(): Promise<void> {
  const s = load();
  const anyMesh = VISUAL_CATEGORIES.some((cat) => effectiveTier(cat, s[cat]) === 'mesh');
  return anyMesh ? warmWebGL() : Promise.resolve();
}

export function ensureWebGLForCurrentStyle(): Promise<void> {
  return warmWebGLIfPreviouslyEnabled();
}

/** Sync accessors render.ts uses each frame. Resolve to no-ops until the
 *  dynamic-imported overlay module has finished loading. Keeps render.ts
 *  free of any static reference to webgl/overlay.ts, which lets Vite
 *  split three.js into its own chunk. */
export interface WebGLOverlayCall {
  asteroids: ReadonlyArray<Asteroid>;
  ufos: ReadonlyArray<Ufo>;
  powerups: ReadonlyArray<PowerUp>;
  /** Every live ship to render as a 3D mesh. Empty array = no meshes
   *  (the caller wants the 2D path, e.g. during an intertitle hold or
   *  with ship tier set to shaded/vector). Index in this array IS the
   *  player slot — the overlay caches per-slot mesh handles, so a slot
   *  that disappears from this array gets its mesh hidden, and a new
   *  slot rebuilds. */
  ships: ReadonlyArray<Ship | null | undefined>;
  /** Per-slot identity tint (index == player slot, parallel to `ships`). Set
   *  in 2P couch / duel so each ship mesh is re-hued to its HUD chip colour
   *  (P1 green, P2 blue) and players can tell which ship is theirs. Undefined
   *  entry or absent array = stock cyan hull (solo / title). */
  shipTints?: ReadonlyArray<string | undefined>;
  /** Sim clock (ms) for time-based overlay effects such as the shield
   *  dome's expiry fade. Wall-clock would mismatch the sim-clock deadlines
   *  the gameplay state now carries (B3 determinism). */
  elapsed: number;
  dpr: number;
  scale: number;
  tx: number;
  ty: number;
  /** World dimensions represented by the camera projection. Defaults to
   *  the classic 1280x720 playfield; deathmatch passes its larger arena. */
  worldW?: number;
  worldH?: number;
  /** World-X seam offsets for the portrait follow camera. The overlay
   *  renders the scene once per entry so mesh entities wrap at the world
   *  edge. Absent (treated as [0]) outside portrait-follow. */
  wrapXs?: number[];
}
let overlayRenderFn: ((opts: WebGLOverlayCall) => void) | null = null;
let overlayShipExplosionFn: ((pos: { x: number; y: number }, vel: { x: number; y: number }, rot: number) => void) | null = null;
let overlayClearShipChunksFn: (() => void) | null = null;
let overlayPrewarmMeshesFn: (() => void) | null = null;
let overlayReady = false;
let overlayUsableFn: (() => boolean) | null = null;
export function isWebGLOverlayReady(): boolean { return overlayReady && (overlayUsableFn?.() ?? false); }
export function callWebGLOverlay(opts: WebGLOverlayCall): void {
  overlayRenderFn?.(opts);
}
/** Trigger the WebGL ship-mesh explosion. Safe to call even if the
 *  overlay hasn't loaded yet — silently no-ops. Callers should still
 *  gate on isWebGLOverlayReady() so the 2D-debris fallback runs when
 *  WebGL isn't available. */
export function callWebGLShipExplosion(pos: { x: number; y: number }, vel: { x: number; y: number }, rot: number): void {
  overlayShipExplosionFn?.(pos, vel, rot);
}
/** Drop any live ship-explosion chunks. Called when the final-life
 *  cleanup wipes particle/debris pools so the death replay starts
 *  from a clean overlay too. */
export function callWebGLClearShipChunks(): void {
  overlayClearShipChunksFn?.();
}

/** Compile the mesh overlay's expensive first-use material/geometry variants
 *  after the player has started a run. This keeps mobile boot quiet while
 *  moving UFO/station first-spawn hitches into the wave-start downtime. */
export async function prewarmWebGLMeshesForCurrentStyle(): Promise<void> {
  const s = load();
  const anyMesh = VISUAL_CATEGORIES.some((cat) => effectiveTier(cat, s[cat]) === 'mesh');
  if (!anyMesh) return;
  await warmWebGL();
  overlayPrewarmMeshesFn?.();
}

let warmPromise: Promise<void> | null = null;
async function warmWebGL(): Promise<void> {
  if (overlayReady) return;
  if (warmPromise) return warmPromise;
  warmPromise = (async () => {
    try {
      const mod = await import('./webgl/overlay.js');
      await mod.ensureWebGLOverlay();
      overlayUsableFn = () => mod.getReadyOverlay() !== null;
      overlayRenderFn = mod.renderOverlay;
      overlayShipExplosionFn = mod.spawnShipMeshExplosion;
      overlayClearShipChunksFn = mod.clearShipChunks;
      overlayPrewarmMeshesFn = mod.prewarmWebGLOverlayMeshes;
      overlayReady = true;
    } catch (e) {
      // If three.js fails to load (offline, sw bug, etc.), render code
      // keeps falling back to shaded — no game-breaking failure mode.
      console.warn('[visual-style] WebGL overlay load failed', e);
    } finally {
      if (!overlayReady) warmPromise = null;
    }
  })();
  return warmPromise;
}

/** True iff every category is set to the same tier — used by the quick-pick
 *  row to highlight which preset is active. */
export function visualStyleIsUniform(tier: VisualTier): boolean {
  const s = load();
  return s.asteroid === tier && s.ship === tier && s.bullet === tier && s.particle === tier;
}

/** True iff a visual-tier preference has been stored for this origin
 *  (vs. running on the flavour default). Lets a surface apply its own
 *  default without overriding a real player choice. */
export function hasStoredVisualStyle(): boolean {
  try { return localStorage.getItem(KEY) !== null; }
  catch { return false; }
}
